import { Component, Input, Output, EventEmitter, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslationService } from '../../../services/translation.service';
import { DatePickerComponent } from '../date-picker/date-picker.component';
import { TimePickerComponent } from '../time-picker/time-picker.component';
import {
  formatScheduledTime,
  formatAppointmentDate,
  formatAppointmentTime,
} from '../../utils/date-time.utils';
import {
  AppointmentsService,
  RejectAppointmentPayload,
} from '../../../components/schedule/services/appointments.service';
import { AuthService } from '../../../core/services/auth.service';
import { ToastService } from '../../../services/toast.service';

export interface CancelData {
  reason: string;
  customReason?: string;
  proposedDate: string;
  proposedTime: string;
  doctorNote: string;
}

export interface AppointmentData {
  id?: string;
  patientName?: string;
  patient?: { name: string } | string;
  title?: string;
  time?: string;
  date?: string;
  type?: string;
}

@Component({
  selector: 'app-cancel-appointment-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePickerComponent, TimePickerComponent],
  templateUrl: './cancel-appointment-modal.component.html',
  styleUrl: './cancel-appointment-modal.component.scss',
})
export class CancelAppointmentModalComponent {
  @Input() isOpen: boolean = false;
  @Input() appointment: AppointmentData | null = null;
  @Output() modalClose = new EventEmitter<void>();
  @Output() appointmentCancel = new EventEmitter<CancelData>();

  cancelData = signal<CancelData>({
    reason: '',
    customReason: '',
    proposedDate: '',
    proposedTime: '',
    doctorNote: '',
  });

  // Computed signal to check if "other" reason is selected
  isOtherReasonSelected = computed(() => this.cancelData().reason === 'other');

  // Computed signal for minimum date (tomorrow)
  minDate = computed(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow;
  });

  // Computed signal to format appointment date based on language
  formattedAppointmentDate = computed(() => {
    const currentLang = this.translationService.getCurrentLanguage();
    return formatAppointmentDate(this.appointment?.date, currentLang);
  });

  // Computed signal to format appointment time based on language
  formattedAppointmentTime = computed(() => {
    const currentLang = this.translationService.getCurrentLanguage();
    return formatAppointmentTime(this.appointment?.time, currentLang);
  });

  // Computed signal for auto-generated doctor note
  generatedDoctorNote = computed(() => {
    const data = this.cancelData();
    const patientName = this.getPatientName();

    if (!data.reason || !data.proposedDate || !data.proposedTime) {
      return '';
    }

    const reason = data.reason === 'other' ? data.customReason || '' : data.reason;
    const currentLang = this.translationService.getCurrentLanguage();

    const [year, month, day] = data.proposedDate.split('-');

    if (currentLang === 'fr') {
      // Format: DD/MM/YYYY et heure en format 24h (8h30, 17h00)
      const formattedDate = `${day}/${month}/${year}`;
      const formattedTime = data.proposedTime.replace(':', 'h');
      return `Bonjour ${patientName}, pour raison de ${reason} je vais devoir reporter votre rendez-vous médical au ${formattedDate} à ${formattedTime}.`;
    } else {
      // Format: YYYY-MM-DD et heure avec AM/PM
      const formattedDate = data.proposedDate;
      const [hours, minutes] = data.proposedTime.split(':');
      const hour = Number.parseInt(hours, 10);
      const period = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      const formattedTime = `${hour12}:${minutes} ${period}`;
      return `Hello ${patientName}, due to ${reason} I will need to reschedule your medical appointment to ${formattedDate} at ${formattedTime}.`;
    }
  });

  private readonly appointmentsService = inject(AppointmentsService);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);

  constructor(public translationService: TranslationService) {}

  closeModal() {
    this.resetForm();
    this.modalClose.emit();
  }

  confirmCancel() {
    if (this.isCancelFormValid() && this.appointment?.id) {
      const data = this.cancelData();
      const user = this.authService.currentUser();
      const hospitalId = user?.hospital_id || '';

      // Combine date and time to create new_scheduled_time
      const newScheduledTime = formatScheduledTime(data.proposedDate, data.proposedTime);

      // Determine the reason to send
      const reason = data.reason === 'other' ? data.customReason || '' : data.reason;

      const payload: RejectAppointmentPayload = {
        new_scheduled_time: newScheduledTime,
        reason: reason,
        doctor_note: data.doctorNote || '',
      };

      this.appointmentsService
        .declineAppointment(hospitalId, this.appointment.id, payload)
        .subscribe({
          next: () => {
            this.toastService.showSuccess(this.translationService.translate('cancel.success'));
            this.appointmentCancel.emit(this.cancelData());
            this.resetForm();
            this.closeModal();
          },
          error: () => {
            this.toastService.showError(this.translationService.translate('cancel.error'));
          },
        });
    }
  }

  updateCancelField<K extends keyof CancelData>(field: K, value: CancelData[K]) {
    this.cancelData.update((data) => ({
      ...data,
      [field]: value,
    }));
  }

  isCancelFormValid(): boolean {
    const data = this.cancelData();
    const baseValidation = !!(data.reason && data.proposedDate && data.proposedTime);

    // If "other" is selected, also require customReason
    if (data.reason === 'other') {
      if (!baseValidation || !data.customReason?.trim()) {
        return false;
      }
    } else if (!baseValidation) {
      return false;
    }

    // Check that the proposed date/time is not the same as the current appointment
    if (this.appointment?.date && this.appointment?.time) {
      const currentDateTime = `${this.appointment.date}T${this.appointment.time}`;
      const proposedDateTime = `${data.proposedDate}T${data.proposedTime}`;
      if (currentDateTime === proposedDateTime) {
        return false;
      }
    }

    return true;
  }

  resetForm() {
    this.cancelData.set({
      reason: '',
      customReason: '',
      proposedDate: '',
      proposedTime: '',
      doctorNote: '',
    });
  }

  getPatientName(): string {
    if (this.appointment?.patientName) {
      return this.appointment.patientName;
    }
    if (this.appointment?.patient) {
      if (typeof this.appointment.patient === 'string') {
        return this.appointment.patient;
      }
      return this.appointment.patient.name;
    }
    if (this.appointment?.title) {
      return this.appointment.title;
    }
    return 'John Smith';
  }

  onSelectChange(event: Event, field: keyof CancelData): void {
    const target = event.target as HTMLSelectElement;
    if (target) {
      this.updateCancelField(field, target.value);
    }
  }

  onInputChange(event: Event, field: keyof CancelData): void {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    if (target) {
      this.updateCancelField(field, target.value);
    }
  }

  onDateChange(date: Date | null): void {
    if (date) {
      // Convert Date to YYYY-MM-DD string
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      this.updateCancelField('proposedDate', `${year}-${month}-${day}`);
      // Update doctor note with generated message
      this.updateCancelField('doctorNote', this.generatedDoctorNote());
    } else {
      this.updateCancelField('proposedDate', '');
    }
  }

  onTimeChange(time: string): void {
    this.updateCancelField('proposedTime', time);
    // Update doctor note with generated message
    this.updateCancelField('doctorNote', this.generatedDoctorNote());
  }

  onReasonChange(event: Event, field: keyof CancelData): void {
    this.onSelectChange(event, field);
    // Update doctor note when reason changes
    this.updateCancelField('doctorNote', this.generatedDoctorNote());
  }
}
